import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Input, Label, Row, Table } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import { getCustodyFileUrl, getVoyage, getVoyages, saveVoyage, setVoyageMilestone, undoVoyageLoad, voyageCars } from '../devir-teslim/api';
import '../devir-teslim/devir-teslim.css';

// Sevkiyat İşlemleri > Gemi Seferleri (İhracat / İthalat): a voyage is our shipment number (SHP + year + 6 digits) on a vessel
// defined in Tanımlamalar > Gemiler, with the operator's voyage number. Cars are put on it (paste or Excel), the
// loading list and the discharge list from the destination are recorded as movements (custody records port → vessel →
// discharge port, the file kept as evidence). Loading start → discharge finished is the voyage transit SLA; its limit is
// the voyage's ETD→ETA plus the rule's tolerance (036).
// Role group 'Sevkiyat İşlemleri - Gemi Planlama' (032_vessels_voyages.sql).
const ROLE_GROUP = 39;

const STATUS = {
    PLANLANDI: ['Planlandı', 'secondary'], YUKLEMEDE: ['Yüklemede', 'info'], YOLDA: ['Yolda', 'primary'],
    TAHLIYEDE: ['Tahliyede', 'warning'], TAMAMLANDI: ['Tamamlandı', 'success'], IPTAL: ['İptal', 'dark'],
};
const MILESTONES = [
    ['loading_started', 'Yükleme başladı'], ['loading_finished', 'Yükleme bitti'], ['departed', 'Gemi kalktı'],
    ['arrived', 'Gemi vardı'], ['discharge_started', 'Tahliye başladı'], ['discharge_finished', 'Tahliye bitti'],
];
const ACTIONS = [
    ['PLAN', 'Sefere araç ekle (plan)'], ['LOAD', 'Yükleme listesi (limandan gemiye)'],
    ['DISCHARGE', 'Tahliye listesi (varış limanı)'], ['REMOVE', 'Plandan çıkar (yüklenmemiş)'],
    ['UNDO_LOAD', 'Yüklemeyi geri al (stoğa dön)'],
];
const EVENT = {
    SEFER_ACILDI: 'Sefer açıldı', SEFER_DEGISTI: 'Sefer bilgisi değişti', SEFER_IPTAL: 'Sefer iptal', ZAMAN: 'Sefer zamanı',
    LISTE_ITHAL: 'Gemi listesi (Şase Tanımlama)', TERMINAL_YUKLEME: 'El terminaliyle yükleme', TAHLIYE: 'Tahliye', TAHLIYE_GERI_ALINDI: 'Tahliye geri alındı',
    LISTE_PLAN: 'Plana ekleme', LISTE_REMOVE: 'Plandan çıkarma', LISTE_LOAD: 'Yükleme listesi', LISTE_DISCHARGE: 'Tahliye listesi',
};

const fmt = (t) => (t ? moment(t).format('DD.MM.YYYY HH:mm') : '—');
const forInput = (t) => (t ? moment(t).format('YYYY-MM-DDTHH:mm') : '');
const err = (e, msg) => toast.error(e?.response?.data?.error || msg);

const EMPTY = { id: 0, vessel_id: '', operator_voyage_no: '', direction: 'IHRACAT', load_location_id: '', discharge_location_id: '',
    load_locode: '', discharge_locode: '', etd: '', eta: '', note: '' };

const VoyageForm = ({ md, initial, onSaved, onCancel }) => {
    const [f, setF] = useState(initial);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

    useEffect(() => {
        setF((prev) => {
            let next = { ...prev };
            let changed = false;
            if (next.load_location_id && !next.load_locode) {
                const lp = md.ports.find((p) => String(p.id) === String(next.load_location_id));
                if (lp && lp.locode) {
                    next.load_locode = lp.locode;
                    changed = true;
                }
            }
            if (next.discharge_location_id && !next.discharge_locode) {
                const dp = md.ports.find((p) => String(p.id) === String(next.discharge_location_id));
                if (dp && dp.locode) {
                    next.discharge_locode = dp.locode;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [md.ports]);

    const handlePortChange = (locationKey, locodeKey) => (e) => {
        const portId = e.target.value;
        const selectedPort = md.ports.find((p) => String(p.id) === String(portId));
        setF((prev) => ({
            ...prev,
            [locationKey]: portId,
            [locodeKey]: (selectedPort && selectedPort.locode) ? selectedPort.locode : (portId ? prev[locodeKey] : ''),
        }));
    };

    const save = async () => {
        try {
            const res = await saveVoyage({ ...f, vessel_id: Number(f.vessel_id) || 0, load_location_id: Number(f.load_location_id) || 0,
                discharge_location_id: Number(f.discharge_location_id) || 0 });
            toast.success(f.id ? 'Sefer güncellendi' : `Sefer açıldı: ${res.voyage_no}`);
            onSaved(res.id);
        } catch (e) { err(e, 'Kaydedilemedi'); }
    };
    const port = (locationKey, locodeKey) => (
        <Input type="select" value={f[locationKey]} onChange={handlePortChange(locationKey, locodeKey)}>
            <option value="">— seçiniz —</option>
            {md.ports.map((p) => (
                <option key={p.id} value={p.id}>
                    {p.name} ({p.country}){p.locode ? ` · ${p.locode}` : ''}{p.operator ? ` · ${p.operator}` : ''}
                </option>
            ))}
        </Input>
    );
    return (
        <div className="dt-draft mb-3">
            <Row>
                <Col md="3"><Label>Gemi</Label>
                    <Input type="select" value={f.vessel_id} onChange={set('vessel_id')}>
                        <option value="">— seçiniz —</option>
                        {md.vessels.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.operator_name}</option>)}
                    </Input>
                    <small className="text-muted">Listede yoksa: <Link to="/tanimlamalar/gemiler">Tanımlamalar &gt; Gemiler</Link></small></Col>
                <Col md="2"><Label>Armatör sefer no</Label><Input value={f.operator_voyage_no} onChange={set('operator_voyage_no')} placeholder="ör. 2644E" /></Col>
                <Col md="2"><Label>Yön</Label>
                    <Input type="select" value={f.direction} onChange={set('direction')}>
                        <option value="IHRACAT">İhracat</option><option value="ITHALAT">İthalat</option>
                    </Input></Col>
                <Col md="2"><Label>ETD (planlanan kalkış)</Label><Input type="datetime-local" value={f.etd} onChange={set('etd')} /></Col>
                <Col md="2"><Label>ETA (planlanan varış)</Label><Input type="datetime-local" value={f.eta} onChange={set('eta')} /></Col>
            </Row>
            <Row className="mt-2">
                <Col md="4"><Label>Yükleme limanı</Label>{port('load_location_id', 'load_locode')}</Col>
                <Col md="1"><Label>UN/LOCODE</Label><Input value={f.load_locode} maxLength={6} placeholder="TRGEM" onChange={set('load_locode')} /></Col>
                <Col md="4"><Label>Tahliye limanı</Label>{port('discharge_location_id', 'discharge_locode')}</Col>
                <Col md="1"><Label>UN/LOCODE</Label><Input value={f.discharge_locode} maxLength={6} placeholder="ITGOA" onChange={set('discharge_locode')} /></Col>
            </Row>
            <Row className="mt-2">
                <Col md="8"><Label>Not</Label><Input value={f.note} onChange={set('note')} /></Col>
                <Col md="4" className="d-flex align-items-end">
                    {f.id > 0 && <Label className="mr-3 mb-2"><Input type="checkbox" checked={!!f.cancelled} onChange={(e) => setF({ ...f, cancelled: e.target.checked })} /> Sefer iptal</Label>}
                    <Button color="primary" className="mr-1" onClick={save}>{f.id ? 'Kaydet' : 'Seferi aç'}</Button>
                    <Button color="secondary" onClick={onCancel}>Vazgeç</Button></Col>
            </Row>
        </div>
    );
};

const Milestone = ({ voyage, code, name, canEdit, onSaved }) => {
    const [edit, setEdit] = useState(false);
    const [v, setV] = useState(forInput(voyage[`${code}_at`]) || moment().format('YYYY-MM-DDTHH:mm'));
    const save = async (at) => {
        try { await setVoyageMilestone(voyage.id, code, at); setEdit(false); onSaved(); } catch (e) { err(e, 'Kaydedilemedi'); }
    };
    const at = voyage[`${code}_at`];
    return (
        <Col className="dt-milestone border rounded p-2 mr-2 mb-2" style={{ minWidth: 150 }}>
            <div className="text-muted small">{name}</div>
            {!edit && <div><b>{fmt(at)}</b>
                {canEdit && voyage.status !== 'IPTAL' && <Button size="xs" color="link" className="p-0 ml-2" onClick={() => setEdit(true)}>{at ? 'değiştir' : 'gir'}</Button>}</div>}
            {edit && <div>
                <Input bsSize="sm" type="datetime-local" value={v} onChange={(e) => setV(e.target.value)} />
                <Button size="sm" color="primary" className="mt-1 mr-1" onClick={() => save(v)}>Kaydet</Button>
                {at && <Button size="sm" color="danger" outline className="mt-1 mr-1" onClick={() => save('')}>Sil</Button>}
                <Button size="sm" color="secondary" className="mt-1" onClick={() => setEdit(false)}>Vazgeç</Button>
            </div>}
        </Col>
    );
};

const ListDownload = ({ ev }) => {
    if (!ev.list_id) return null;
    const open = async () => {
        try {
            const url = await getCustodyFileUrl(`list:${ev.list_id}`);
            const a = document.createElement('a');
            a.href = url; a.download = ev.file_name || `liste-${ev.list_id}`; a.click();
        } catch (e) { err(e, 'Liste indirilemedi'); }
    };
    return <Button size="sm" color="link" className="p-0" onClick={open}>{ev.file_name || 'liste'}</Button>;
};

// result of the last list, car by car; stays visible after the voyage completes (the list form then closes)
const ListResult = ({ result }) => (
    <Table size="sm" className="mb-3">
        <thead><tr><th>Şase</th><th>Sonuç</th><th>Devir no</th><th>Açıklama</th></tr></thead>
        <tbody>{result.results.map((r) => (
            <tr key={r.chassis_no}><td>{r.chassis_no}</td>
                <td>{r.ok ? <Badge color={r.message ? 'warning' : 'success'}>{r.message ? 'uyarı' : 'tamam'}</Badge> : <Badge color="danger">hata</Badge>}</td>
                <td>{r.handover_no || ''}</td><td>{r.message || ''}</td></tr>
        ))}</tbody>
    </Table>
);

const CarsAction = ({ voyage, onDone }) => {
    const [action, setAction] = useState('PLAN');
    const [text, setText] = useState('');
    const [file, setFile] = useState(null);
    const [at, setAt] = useState('');
    const [busy, setBusy] = useState(false);
    const vins = text.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
    const run = async () => {
        setBusy(true);
        try {
            if (action === 'UNDO_LOAD') {
                let done = 0; let failed = 0;
                const results = [];
                for (const vin of vins) {
                    try {
                        const r = await undoVoyageLoad(voyage.id, vin);
                        if (r && r.success) { done += 1; results.push({ chassis_no: vin, ok: true }); }
                        else { failed += 1; results.push({ chassis_no: vin, ok: false, message: 'başarısız' }); }
                    } catch (e) { failed += 1; results.push({ chassis_no: vin, ok: false, message: e?.response?.data?.error || 'hata' }); }
                }
                toast[failed ? 'warning' : 'success'](`${done} araç geri alındı${failed ? `, ${failed} hata` : ''}`);
                setText(''); setFile(null);
                onDone({ done, failed, results });
            } else {
                const res = await voyageCars({ id: voyage.id, action, chassis_nos: file ? [] : vins, at }, file);
                toast[res.failed ? 'warning' : 'success'](`${res.done} araç işlendi${res.failed ? `, ${res.failed} hata` : ''}`);
                setText(''); setFile(null);
                onDone(res);
            }
        } catch (e) { err(e, 'İşlenemedi'); }
        setBusy(false);
    };
    const withTime = action === 'LOAD' || action === 'DISCHARGE';
    const supportsFile = action !== 'UNDO_LOAD';
    return (
        <div className="dt-draft mb-3">
            <Row>
                <Col md="4"><Label>İşlem</Label>
                    <Input type="select" value={action} onChange={(e) => setAction(e.target.value)}>
                        {ACTIONS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
                    </Input></Col>
                {supportsFile && <Col md="5"><Label>Excel / CSV listesi (şase sütunu; {withTime ? 'varsa tarih sütunu satırın zamanıdır' : 'başlık: Şase / VIN'})</Label>
                    <Input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files[0] || null)} key={file ? 'f' : 'nf'} /></Col>}
                {withTime && <Col md="3"><Label>{action === 'LOAD' ? 'Yükleme' : 'Tahliye'} zamanı (tarihsiz satırlar; boş = şimdi)</Label>
                    <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} /></Col>}
            </Row>
            {!file && <Row className="mt-2"><Col>
                <Label>veya şaseleri yapıştırın ({vins.length})</Label>
                <Input type="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="her satıra bir şase" />
            </Col></Row>}
            <div className="mt-2"><Button color="primary" disabled={busy || (!file && vins.length === 0)} onClick={run}>Uygula</Button>
                <span className="text-muted ml-2 small">Liste, hareketin kaydı olarak saklanır (devir kayıtlarında kaynak: LİSTE).</span></div>
        </div>
    );
};

const VoyageDetail = ({ id, md, canEdit, onChanged }) => {
    const [d, setD] = useState(null);
    const [editing, setEditing] = useState(false);
    const [result, setResult] = useState(null);
    const load = useCallback(() => getVoyage(id).then(setD).catch((e) => err(e, 'Sefer alınamadı')), [id]);
    useEffect(() => { load(); }, [load]);
    if (!d) return null;
    const v = d.voyage;
    const changed = () => { load(); onChanged(); };
    const transit = v.transit_hours != null ? `${v.transit_hours} saat (${(v.transit_hours / 24).toFixed(1)} gün)` : '—';
    const over = v.transit_hours != null && v.transit_hours > v.sla_hours;
    const active = d.cars.filter((c) => !c.removed_at);
    return (
        <Card><CardBody>
            <div className="d-flex align-items-center mb-2">
                <h5 className="mb-0 mr-3">{v.voyage_no}</h5>
                <Badge color={STATUS[v.status][1]} className="mr-3">{STATUS[v.status][0]}</Badge>
                <span><b>{v.vessel_name}</b> · {v.operator_name} · armatör sefer no <b>{v.operator_voyage_no}</b> · {v.direction === 'IHRACAT' ? 'İhracat' : 'İthalat'}</span>
                {canEdit && !editing && <Button size="sm" color="link" className="ml-auto" onClick={() => setEditing(true)}>Sefer bilgisini değiştir</Button>}
            </div>
            {editing && <VoyageForm md={md} initial={{ ...EMPTY, ...v, etd: forInput(v.etd), eta: forInput(v.eta),
                load_locode: v.load_locode || '', discharge_locode: v.discharge_locode || '', note: v.note || '' }}
                onSaved={() => { setEditing(false); changed(); }} onCancel={() => setEditing(false)} />}
            <div className="mb-2">
                {v.load_location}{v.load_locode ? ` (${v.load_locode})` : ''} → {v.discharge_location}{v.discharge_locode ? ` (${v.discharge_locode})` : ''}
                <span className="ml-3 text-muted">ETD {fmt(v.etd)} · ETA {fmt(v.eta)}</span>
                {v.note && <span className="ml-3 text-muted">Not: {v.note}</span>}
            </div>
            <Row className="mx-0">
                {MILESTONES.map(([code, name]) => <Milestone key={code + (v[`${code}_at`] || '')} voyage={v} code={code} name={name} canEdit={canEdit} onSaved={changed} />)}
            </Row>
            <div className="mb-3">
                Sefer süresi (yükleme başlangıcı → tahliye bitişi): <b className={over ? 'text-danger' : ''}>{transit}</b>
                <span className="text-muted"> · SLA {v.sla_hours} saat{v.etd && v.eta ? ' (ETD→ETA + tolerans)' : ''}{v.discharge_finished_at ? '' : (v.loading_started_at ? ' · sürüyor' : '')}</span>
                <span className="ml-3">Planlı <b>{v.planned}</b> · Yüklenen <b>{v.loaded}</b> · Tahliye edilen <b>{v.discharged}</b></span>
            </div>
            {v.direction === 'ITHALAT' && <div className="dt-draft mb-3 text-muted">İthalat seferi: araçlar <b>Veri Yükleme &gt; Şase Tanımlama</b>'da
                GEMİ + bu sefer seçilerek eklenir (gemi listesi). Tahliye, el terminali (4-Gemi Tahliye) veya Gemi Tahliye Bilgisi yüklemesiyle
                araç araç kendiliğinden işlenir; liste bitince tahliye bitti zamanı dolar. Yükleme başladı zamanını (yurtdışı limanda) armatör
                bilgisinden girin — sefer süresi (SLA) oradan başlar.</div>}
            {canEdit && v.direction === 'IHRACAT' && v.status !== 'IPTAL' && v.status !== 'TAMAMLANDI' && <CarsAction voyage={v} onDone={(res) => { setResult(res); changed(); }} />}
            {result && <ListResult result={result} />}
            <Table size="sm" bordered responsive>
                <thead><tr><th>Şase</th><th>Marka / model</th><th>Şu an (depo / bölge)</th><th>Sevk emri hedefi</th><th>Plana alındı</th>
                    <th>Yüklendi</th><th>Tahliye</th></tr></thead>
                <tbody>
                    {active.length === 0 && <tr><td colSpan={7} className="text-muted">Seferde araç yok.</td></tr>}
                    {d.cars.map((c) => (
                        <tr key={c.chassis_no} className={c.removed_at ? 'text-muted' : ''} style={c.removed_at ? { textDecoration: 'line-through' } : {}}>
                            <td>{c.chassis_no}</td>
                            <td>{c.brand} {c.model}</td><td>{c.place || '—'}</td><td>{c.order_destination || '—'}</td>
                            <td>{fmt(c.planned_at)}</td>
                            <td>{fmt(c.loaded_at)}{c.load_handover_no && <div className="small text-muted">{c.load_handover_no}</div>}</td>
                            <td>{fmt(c.discharged_at)}{c.discharge_handover_no && <div className="small text-muted">{c.discharge_handover_no}</div>}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <h6 className="mt-3">Sefer geçmişi</h6>
            <Table size="sm" responsive>
                <thead><tr><th>Zaman</th><th>Kim</th><th>Ne</th><th>Ayrıntı</th><th>Liste</th></tr></thead>
                <tbody>{d.events.map((e, i) => (
                    <tr key={i}><td>{fmt(e.at)}</td><td>{e.by_name}</td><td>{EVENT[e.kind] || e.kind}</td>
                        <td className="small">{e.kind.startsWith('SEFER_') ? '' : e.detail}</td><td><ListDownload ev={e} /></td></tr>
                ))}</tbody>
            </Table>
        </CardBody></Card>
    );
};

const GemiPlanlama = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const canEdit = hasRole('gemi_sefer_write');
    const [onlyOpen, setOnlyOpen] = useState(true);
    const [direction, setDirection] = useState('');
    const [md, setMd] = useState({ voyages: [], vessels: [], ports: [] });
    const [adding, setAdding] = useState(false);
    const [selected, setSelected] = useState(null);
    const load = useCallback(() => getVoyages(onlyOpen).then(setMd).catch((e) => err(e, 'Seferler alınamadı')), [onlyOpen]);
    useEffect(() => { load(); }, [load]);
    const shown = md.voyages.filter((v) => !direction || v.direction === direction);
    return (
        <>
            <Breadcrumb parent="Sevkiyat İşlemleri" current="Gemi Seferleri (İhracat / İthalat)" />
            <Card><CardBody>
                <div className="d-flex align-items-center mb-2" style={{ gap: 16 }}>
                    <span className="text-muted">Sefer = gemi + armatör sefer no; bizim numaramız SHP+yıl+6 hane. Gemi, Tanımlamalar &gt; Gemiler'den seçilir.</span>
                    <Input type="select" className="ml-auto" style={{ maxWidth: 170 }} value={direction} onChange={(e) => setDirection(e.target.value)}>
                        <option value="">İhracat + İthalat</option><option value="IHRACAT">İhracat</option><option value="ITHALAT">İthalat</option>
                    </Input>
                    <Label className="mb-0 text-nowrap"><Input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Sadece açık seferler</Label>
                    {canEdit && !adding && <Button color="primary" onClick={() => setAdding(true)}>+ Yeni sefer</Button>}
                </div>
                {adding && <VoyageForm md={md} initial={{ ...EMPTY, direction: direction || 'IHRACAT' }} onCancel={() => setAdding(false)}
                    onSaved={(id) => { setAdding(false); setSelected(id); load(); }} />}
                <Table size="sm" hover responsive>
                    <thead><tr><th>Sefer no</th><th>Yön</th><th>Gemi</th><th>Armatör sefer no</th><th>Güzergâh</th><th>ETD</th><th>ETA</th><th>Durum</th>
                        <th>Planlı / yüklenen / tahliye</th><th>Sefer süresi</th></tr></thead>
                    <tbody>
                        {shown.length === 0 && <tr><td colSpan={10} className="text-muted">Sefer yok.</td></tr>}
                        {shown.map((v) => (
                            <tr key={v.id} onClick={() => setSelected(v.id)} style={{ cursor: 'pointer' }} className={selected === v.id ? 'table-active' : ''}>
                                <td><b>{v.voyage_no}</b></td><td>{v.direction === 'IHRACAT' ? 'İhracat' : 'İthalat'}</td><td>{v.vessel_name}<div className="small text-muted">{v.operator_name}</div></td>
                                <td>{v.operator_voyage_no}</td><td>{v.load_location} → {v.discharge_location}</td>
                                <td>{fmt(v.etd)}</td><td>{fmt(v.eta)}</td>
                                <td><Badge color={STATUS[v.status][1]}>{STATUS[v.status][0]}</Badge></td>
                                <td>{v.planned} / {v.loaded} / {v.discharged}</td>
                                <td className={v.transit_hours > v.sla_hours ? 'text-danger' : ''}>{v.transit_hours != null ? `${v.transit_hours} sa` : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </CardBody></Card>
            {selected && <VoyageDetail key={selected} id={selected} md={md} canEdit={canEdit} onChanged={load} />}
        </>
    );
};

export default GemiPlanlama;
